import React, { useState } from 'react';
import { Event } from '../../data/eventsService';

type EventDetailModalProps = {
  event: Event;
  onClose: () => void;
};

export default function EventDetailModal({ event, onClose }: EventDetailModalProps) {
  const [showRawJson, setShowRawJson] = useState(false);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const renderMetadataValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-[#2C2731]/50 italic">null</span>;
    }

    if (typeof value === 'boolean') {
      return <span className="text-[#2C2731] font-medium">{value.toString()}</span>;
    }

    if (typeof value === 'number') {
      return <span className="text-[#2C2731] font-medium">{value.toLocaleString()}</span>;
    }

    if (typeof value === 'string') {
      // Check if it's a URL
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            {value}
          </a>
        );
      }
      // Check if it's a UUID-like string (user_id, guide_id, etc.)
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        return <code className="text-xs bg-[#F2EAC3] px-2 py-1 rounded">{value}</code>;
      }
      return <span className="text-[#2C2731]">{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-[#2C2731]/50 italic">empty array</span>;
      }
      return (
        <ul className="list-disc list-inside space-y-1 ml-2">
          {value.map((item, idx) => (
            <li key={idx} className="text-sm">
              {renderMetadataValue(item)}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof value === 'object') {
      return (
        <div className="ml-4 space-y-2 border-l-2 border-[#2C2731]/20 pl-4">
          {Object.entries(value).map(([key, val]) => (
            <div key={key}>
              <span className="font-medium text-[#2C2731]/80">{key}:</span>{' '}
              {renderMetadataValue(val)}
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-[#2C2731]">{String(value)}</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#2C2731]/70 px-4 py-8 overflow-y-auto">
      <div className="w-full max-w-4xl my-8 rounded-2xl border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#2C2731]/20 px-6 py-4">
          <div>
            <h2 className="text-xl font-medium font-recoleta">Event Details</h2>
            <p className="text-sm text-[#2C2731]/70 mt-1">{event.event_name}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Basic Info */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium font-recoleta">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-lg border border-[#2C2731]/20 p-4">
              <div>
                <div className="text-xs text-[#2C2731]/60 uppercase tracking-wide mb-1">
                  Event ID
                </div>
                <code className="text-xs bg-[#F2EAC3] px-2 py-1 rounded">{event.id}</code>
              </div>
              <div>
                <div className="text-xs text-[#2C2731]/60 uppercase tracking-wide mb-1">
                  Event Name
                </div>
                <div className="text-sm font-medium">{event.event_name}</div>
              </div>
              <div>
                <div className="text-xs text-[#2C2731]/60 uppercase tracking-wide mb-1">
                  Timestamp
                </div>
                <div className="text-sm">{formatTimestamp(event.timestamp)}</div>
              </div>
              <div>
                <div className="text-xs text-[#2C2731]/60 uppercase tracking-wide mb-1">
                  Created At
                </div>
                <div className="text-sm">{formatTimestamp(event.created_at)}</div>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium font-recoleta">User Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-lg border border-[#2C2731]/20 p-4">
              <div>
                <div className="text-xs text-[#2C2731]/60 uppercase tracking-wide mb-1">
                  User ID
                </div>
                <code className="text-xs bg-[#F2EAC3] px-2 py-1 rounded">{event.user_id}</code>
              </div>
              <div>
                <div className="text-xs text-[#2C2731]/60 uppercase tracking-wide mb-1">
                  Name
                </div>
                <div className="text-sm">{event.user_name || 'N/A'}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-xs text-[#2C2731]/60 uppercase tracking-wide mb-1">
                  Email
                </div>
                <div className="text-sm">{event.user_email || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Metadata Display */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium font-recoleta">Event Metadata</h3>
              <button
                onClick={() => setShowRawJson(!showRawJson)}
                className="text-xs px-3 py-1.5 rounded-lg border border-[#2C2731] bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450] transition-colors"
              >
                {showRawJson ? 'Show Pretty View' : 'Show Raw JSON'}
              </button>
            </div>

            {showRawJson ? (
              // Raw JSON View
              <div className="bg-[#2C2731] rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-[#FEF6D6] font-mono">
                  {JSON.stringify(event.metadata, null, 2)}
                </pre>
              </div>
            ) : (
              // Pretty View
              <div className="bg-white rounded-lg border border-[#2C2731]/20 p-4">
                {Object.keys(event.metadata).length === 0 ? (
                  <div className="text-sm text-[#2C2731]/50 italic">No metadata</div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(event.metadata).map(([key, value]) => (
                      <div key={key} className="pb-3 border-b border-[#2C2731]/10 last:border-b-0 last:pb-0">
                        <div className="text-sm font-medium text-[#2C2731]/80 mb-1">
                          {key}
                        </div>
                        <div className="text-sm">{renderMetadataValue(value)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#2C2731]/20 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
